import { CartConditionErrorStateType, CartConditionStateType } from './common/cartConditions'
import { ActivePeriodErrorStateType, ActivePeriodStateType, CustomerEligibilityErrorStateType, CustomerEligibilityStateType } from './common/customerConditions'
import { FlowErrorType } from './common/error'
import { PaymentMethodType } from './dashboardAnalytics'

export type AutomationDiscountAppplyCondition = {
  discountId: string
  discountCode: string
}

export type ExecutableConditionType = 'ON_APPLY_REWARDS' | 'ON_APPLY_DISCOUNTS'

export type DisabledEntityType = 'DISABLE_PAYMENT' | 'DISABLE_REWARDS'

export type AutomationApplicableConditions = {
  type: ExecutableConditionType
  count: number
  conditions: AutomationDiscountAppplyCondition[]
}

export type AutomationType = {
  id: string
  title: string
  description: string
  type: DisabledEntityType
  disabledPaymentModes: PaymentMethodType[]
  activePaymentModes: PaymentMethodType[]
  isActive: boolean
  applicableConditions?: AutomationApplicableConditions
}

export type ExecutableCondition = {
  type: ExecutableConditionType
  entity_ids: string[]
  metadata: any
}

export type AutomationConfig = {
  name: string
  desription: string
  type: DisabledEntityType
  disabledEntities: PaymentMethodType[]
  merchantEnabledEntities: PaymentMethodType[]
  isActive: boolean
  executableConditions: ExecutableCondition[]
}

export interface AutomationContextType {
  state: AutomationStateType
  actions: {
    setTitle: (title: string) => void
    setDescription: (description: string) => void
    setCartConditions: (cartConditions: CartConditionStateType) => void
    setCustomerEligibility: (customerEligibility: CustomerEligibilityStateType) => void
    setActivePeriod: (activePeriod: ActivePeriodStateType) => void
    setAutomatedAction: (automatedAction: AutomatedActionType) => void
    setPerformedAction: (performedAction: PerformedActionType) => void
    setJourneyStep: (journeyStep: string) => void
    setAutomation: (automation: AutomationStateType) => void
    addHiddenPaymentMode: (paymentMode: string) => void
    removeHiddenPaymentMode: (paymentMode: string) => void
    setIsActive: (isActive: boolean) => void
    setErrorState: (errorState: AutomationErrorStateType) => void
  }
}

export enum AutomationActionSet {
  SET_TITLE,
  SET_DESCRIPTION,
  SET_CART_CONDITIONS,
  SET_CUSTOMER_ELIGIBILITY,
  SET_ACTIVE_PERIOD,
  SET_AUTOMATED_ACTION,
  SET_PERFORMED_ACTION,
  SET_JOURNEY_STEP,
  SET_AUTOMATION,
  SET_IS_ACTIVE,
  SET_ERROR_STATE
}

export type AutomationActionType = 
  | {
      type: AutomationActionSet.SET_TITLE
      payload: string
    }
  | {
      type: AutomationActionSet.SET_DESCRIPTION
      payload: string
    }
  | {
      type: AutomationActionSet.SET_JOURNEY_STEP
      payload: AutomationFlowJourneySteps
    }
  | {
      type: AutomationActionSet.SET_CART_CONDITIONS
      payload: CartConditionStateType
    }
  | {
      type: AutomationActionSet.SET_CUSTOMER_ELIGIBILITY
      payload: CustomerEligibilityStateType
    }
  | {
      type: AutomationActionSet.SET_ACTIVE_PERIOD
      payload: ActivePeriodStateType
    }
  | {
      type: AutomationActionSet.SET_AUTOMATED_ACTION
      payload: AutomatedActionType
    }
  | {
      type: AutomationActionSet.SET_PERFORMED_ACTION
      payload: PerformedActionType
    }
  | {
      type: AutomationActionSet.SET_AUTOMATION
      payload: AutomationStateType
    }
  | {
      type: AutomationActionSet.SET_IS_ACTIVE
      payload: boolean
    }
  | {
      type: AutomationActionSet.SET_ERROR_STATE
      payload: AutomationErrorStateType
    }

export type AutomationStateType = {
  title: string
  description: string
  automatedAction: AutomatedActionType
  performedAction: PerformedActionType
  cartConditions: CartConditionStateType
  customerEligibility: CustomerEligibilityStateType
  activePeriod: ActivePeriodStateType
  journeyStep: AutomationFlowJourneySteps
  isActive: boolean
  errorState: AutomationErrorStateType
}

export type AutomationErrorStateType = {
  title: FlowErrorType
  automatedAction: { paymentMethodIds: FlowErrorType }
  cartConditions: CartConditionErrorStateType
  performedAction: { appliedDiscounts: FlowErrorType }
  customerEligibility: CustomerEligibilityErrorStateType
  activePeriod: ActivePeriodErrorStateType
}

export enum AutomatedActions {
  DISABLE_REWARDS = 'DISABLE_REWARDS',
  HIDE_PAYMENT_MODES = 'HIDE_PAYMENT_MODES',
  HIDE_SPLIT_COD = 'SHOW_SPLIT_COD'
}

export type AutomatedActionType =
  | {
      type: AutomatedActions.DISABLE_REWARDS
    }
  | {
      type: AutomatedActions.HIDE_SPLIT_COD
    }
  | {
      type: AutomatedActions.HIDE_PAYMENT_MODES
      paymentMethodIds: string[]
    }

export enum PerformedActionsOptions {
  NO_CONDITIONS = 'NO_CONDITIONS',
  REDEEM_REWARDS = 'REDEEM_REWARDS',
  APPLY_DISCOUNTS = 'APPLY_DISCOUNTS',
}

export type PerformedActionType =
  | {
      type: PerformedActionsOptions.NO_CONDITIONS
    }
  | {
      type: PerformedActionsOptions.REDEEM_REWARDS
    }
  | {
      type: PerformedActionsOptions.APPLY_DISCOUNTS
      appliedDiscounts: string[]
      discountsMetadata: AutomationDiscountDataType[]
    }

export enum AutomationFlowJourneySteps {
  SETUP = "setup",
  CART_CONDITIONS = "cart_conditions",
  CUSTOMER_ELIGIBILITY = "customer_eligibility",
  USAGE_LIMITS = "usage_limits",
}

export type AutomatedActionsSelectionType = {
  label: string
  value: AutomatedActions
}

export type MerchantPaymentMode = {
  mode: string
  title: string
  pgName: string
}

export type AutomationDiscountDataType = {
  couponId: string
  id: string
  code: string
  title: string
  isActive: boolean
}

export type AutomationTemplateType = {
  id: string
  name: string
  title: string
  description: string
  image: string
}
